import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { presentationName } from "../../utils/fanHubProfile";
import { brandFilledButtonSx, brandRgba } from "../../utils/brandAssets";
import { colors } from "../../theme";
import { fanHubDialogPaperSx, sportAccent } from "./fanHubStyles";
import {
  FanHubAvatar,
  FanHubDisplayName,
  FanHubHandle,
} from "./FanHubIdentityMark";

const fieldSx = (accent) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    bgcolor: "rgba(255,255,255,0.03)",
    "& fieldset": { borderColor: colors.border },
    "&:hover fieldset": { borderColor: brandRgba(accent, 0.45) },
    "&.Mui-focused fieldset": { borderColor: accent },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: accent },
});

const Field = ({ accent, ...props }) => (
  <TextField fullWidth sx={fieldSx(accent)} {...props} />
);

const FanHubIdentityDialog = ({
  open,
  values,
  onChange,
  onClose,
  onSave,
}) => {
  const accent = sportAccent(values.preferredSport);
  const previewName = presentationName(values) || "Your fan profile";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: fanHubDialogPaperSx(accent) }}
    >
      <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        <Typography
          variant="overline"
          sx={{ letterSpacing: "0.16em", color: "text.secondary" }}
        >
          Identity
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25, mb: 2 }}>
          How you show up
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${brandRgba(accent, 0.28)}`,
            background: `linear-gradient(135deg, ${brandRgba(accent, 0.12)} 0%, rgba(255,255,255,0.02) 100%)`,
          }}
        >
          <FanHubAvatar
            src={values.avatar}
            name={previewName}
            accent={accent}
            sport={values.preferredSport}
            size={72}
          />
          <Box sx={{ minWidth: 0 }}>
            <FanHubHandle username={values.username} />
            <FanHubDisplayName variant="h5">{previewName}</FanHubDisplayName>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
          <Field
            accent={accent}
            label="Username"
            helperText="Unique account and community identifier"
            value={values.username}
            onChange={(event) => onChange({ username: event.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">@</InputAdornment>
              ),
            }}
          />
          <Field
            accent={accent}
            label="Display name"
            helperText="Optional presentation text — does not need to be unique"
            value={values.displayName}
            onChange={(event) => onChange({ displayName: event.target.value })}
          />
          <Box>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
              Photo
            </Typography>
            <Field
              accent={accent}
              label="Photo URL"
              helperText="Paste an image link. File upload comes later."
              value={values.avatar}
              onChange={(event) => onChange({ avatar: event.target.value })}
            />
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 2.5 }}
        >
          Preferred sport stays on the profile header — MLB, NBA, or NFL.
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            mt: 2.5,
          }}
        >
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" onClick={onSave} sx={brandFilledButtonSx}>
            Save
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FanHubIdentityDialog;
